import { useContext } from "react"
import { useTrans, Trans } from "@/i18n"
import { Button } from "@/components/Button"
import { FiChevronRight, CheckIcon } from "@/lib/icons"
import { RedirectContext } from "@/context/redirectContext"
import { useToasts } from "@/context/useToasts"
import {
	useScaEnrollmentStartMutation,
	type ScaEnrollmentStartMutation,
} from "@/api/graphql"
import { captureMessage } from "@/lib/sentry"
import { EXTERNAL_PLATFORM_ZONHUB_MANGOPAY_FAQ_URL } from "@/lib/env"
import { cn } from "@/lib/utils"

interface ScaEnrollmentStepProps {
	isComplete: boolean
}

export const ScaEnrollmentStep = ({ isComplete }: ScaEnrollmentStepProps) => {
	const t = useTrans("investor")

	const statusLabel = t(
		isComplete
			? "investor:identity.copy.mangopay.timeline.status.complete"
			: "investor:identity.copy.mangopay.timeline.status.current",
	)

	return (
		<li className="relative flex gap-4">
			<span
				aria-hidden="true"
				className="absolute top-9 left-4 -ml-px h-[calc(100%+0.5rem)] w-0.5 bg-gray-200"
			/>

			<StepIndicator stepNumber={1} isComplete={isComplete} />

			<div className="flex-1 pt-0.5">
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
					<h3
						className={cn(
							"text-sm font-semibold",
							isComplete ? "text-gray-500" : "text-gray-900",
						)}
					>
						{t(
							"investor:identity.copy.mangopay.timeline.sca.title",
						)}
					</h3>
					<StatusPill isComplete={isComplete} label={statusLabel} />
				</div>
				<div
					className={cn(
						"mt-1 text-sm",
						isComplete ? "text-gray-400" : "text-gray-600",
					)}
				>
					<p>
						{t(
							"investor:identity.copy.mangopay.timeline.sca.description",
						)}
					</p>
					<img
						src="/assets/partners/mangopay_logo.png"
						alt="Mangopay logo"
						className="mt-3 w-28"
					/>
					<p className="mt-2">
						<Trans
							i18nKey="investor:identity.copy.mangopay.copy.faq"
							components={{
								/* oxlint-disable jsx-a11y/anchor-has-content */
								a: (
									<a
										className="text-secondary-700 font-medium hover:underline"
										href={
											EXTERNAL_PLATFORM_ZONHUB_MANGOPAY_FAQ_URL
										}
										target="_blank"
										rel="noreferrer"
									/>
								),
								/* oxlint-enable jsx-a11y/anchor-has-content */
							}}
						/>
					</p>
				</div>
				{!isComplete ? (
					<div className="mt-3">
						<MangopayEnrollmentButton />
					</div>
				) : null}
			</div>
		</li>
	)
}

const StepIndicator = ({
	stepNumber,
	isComplete,
}: {
	stepNumber: number
	isComplete: boolean
}) => {
	const base =
		"relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-medium ring-4 ring-white"

	if (isComplete) {
		return (
			<span
				className={cn(
					base,
					"border-2 border-gray-200 bg-white text-gray-400",
				)}
				aria-hidden="true"
			>
				<CheckIcon className="size-4" />
			</span>
		)
	}

	return (
		<span
			className={cn(
				base,
				"border-secondary-500 text-secondary-700 border-2 bg-white",
			)}
			aria-hidden="true"
		>
			{stepNumber}
		</span>
	)
}

const StatusPill = ({
	isComplete,
	label,
}: {
	isComplete: boolean
	label: string
}) => (
	<span
		className={cn(
			"inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
			isComplete
				? "bg-gray-100 text-gray-500"
				: "bg-yellow-50 text-yellow-800",
		)}
	>
		{label}
	</span>
)

const MangopayEnrollmentButton = () => {
	const t = useTrans("investor")

	const { mutate: startScaEnrollment, isPending } =
		useScaEnrollmentStartMutation()

	const { redirect } = useContext(RedirectContext)
	const { addToast } = useToasts()

	const showWalletUnavailableToast = () => {
		addToast({
			variant: "alert",
			id: "mangopay-sca-wallet-unavailable",
			text: t("investor:identity.copy.mangopay.error.wallet_unavailable"),
		})
	}

	const handleScaEnrollmentSuccess = (result: ScaEnrollmentStartMutation) => {
		const redirectUrl = result?.sca_enrollment_start?.redirect_url
		if (redirectUrl) {
			redirect(redirectUrl)
			return
		}
		captureMessage("Investor unable to enroll SCA", {
			level: "error",
			extra: { reason: "missing_redirect_url", result },
		})
		showWalletUnavailableToast()
	}

	const handleScaEnrollmentError = (error: unknown) => {
		captureMessage("Investor unable to enroll SCA", {
			level: "error",
			extra: { reason: "mutation_error", error },
		})
		showWalletUnavailableToast()
	}

	const handleClick = () => {
		startScaEnrollment(
			{ return_url: window.location.href },
			{
				onSuccess: handleScaEnrollmentSuccess,
				onError: handleScaEnrollmentError,
			},
		)
	}

	return (
		<Button onClick={handleClick} disabled={isPending}>
			{t("profile:contact_details.confirm")}
			<FiChevronRight className="ml-2" />
		</Button>
	)
}
